import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { BACKEND } from '../utils/config'
import { buildAuthHeader } from '../utils/auth'
import { useAppStore } from './useAppStore'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PendingReturn {
  id: string;
  plate: string;
  vehicleName: string;
  clientName: string;
  agencyCode: string;
  completedAt: string;
  agentName: string;
  kmDepart?: number | null;
  kmRetour?: number | null;
  fuelDepart?: number | null;
  fuelRetour?: number | null;
  status: 'pending_upload' | 'confirmed';
}

interface PendingMrcState {
  pendingReturns: PendingReturn[];
  addPendingReturn: (data: Omit<PendingReturn, 'status'>) => void;
  removePendingReturn: (id: string) => void;
  confirmPendingReturn: (id: string) => void;
  // ✅ FIX — activeContractIds optionnel pour le filtrage des alertes périmées
  fetchGlobalAlerts: (agencyCodes: string[], activeContractIds?: string[]) => Promise<void>;
  cleanupAfterSync: (agencyCodes: string[], activeContractIds: string[]) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getAuthHeader(): string | null {
  const { credentials } = useAppStore.getState() as any;
  if (!credentials) return null;
  return buildAuthHeader(credentials);
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const usePendingMrcStore = create<PendingMrcState>()(
  persist(
    (set, get) => ({
      pendingReturns: [],

      // Ajoute un retour en local avec status 'pending_upload'
      addPendingReturn: (data) =>
        set((state) => {
          const entry: PendingReturn = { ...data, status: 'pending_upload' };
          const idx = state.pendingReturns.findIndex((r) => r.id === data.id);
          if (idx >= 0) {
            const list = [...state.pendingReturns];
            list[idx] = entry;
            return { pendingReturns: list };
          }
          return { pendingReturns: [...state.pendingReturns, entry] };
        }),

      // Passe le status local à 'confirmed' (appelé par le worker après POST backend)
      confirmPendingReturn: (id) =>
        set((state) => ({
          pendingReturns: state.pendingReturns.map((r) =>
            r.id === id ? { ...r, status: 'confirmed' as const } : r
          ),
        })),

      // Supprime manuellement (bouton "Saisi ✓") + fire-and-forget DELETE backend
      removePendingReturn: (id) => {
        set((state) => ({
          pendingReturns: state.pendingReturns.filter((r) => r.id !== id),
        }));
        const auth = getAuthHeader();
        if (auth && navigator.onLine) {
          fetch(`${BACKEND}/api/mrc-alerts/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: { 'X-MRC-Auth': auth },
          }).catch((e) => console.warn('[PendingMRC] DELETE backend:', e.message));
        }
      },

      // ✅ FIX — Fetch silencieux depuis le backend
      // activeContractIds (optionnel) : si fourni, les alertes confirmées dont le contrat
      // n'est plus dans MRC sont supprimées du backend (DELETE silencieux) + du local.
      // Cela résout le bug : alertes "retours à saisir" qui persistent après clôture MRC.
      fetchGlobalAlerts: async (agencyCodes, activeContractIds?) => {
        const auth = getAuthHeader();
        if (!auth || !navigator.onLine || !agencyCodes.length) return;
        try {
          const res = await fetch(
            `${BACKEND}/api/mrc-alerts?agency=${encodeURIComponent(agencyCodes.join(','))}`,
            { headers: { 'X-MRC-Auth': auth } }
          );
          if (!res.ok) return;
          const backendAlerts: any[] = await res.json();

          // ✅ FIX — Séparer alertes actives vs périmées si on a la liste des contrats actifs
          let activeBackendAlerts = backendAlerts;
          if (activeContractIds && activeContractIds.length > 0) {
            const staleAlerts = backendAlerts.filter(
              (b) => !activeContractIds.includes(String(b.id))
            );
            activeBackendAlerts = backendAlerts.filter(
              (b) => activeContractIds.includes(String(b.id))
            );

            // DELETE silencieux des alertes périmées (contrat clôturé dans MRC)
            if (staleAlerts.length > 0) {
              console.log(
                `[PendingMRC] 🧹 ${staleAlerts.length} alerte(s) périmée(s) supprimées` +
                ` (contrat clôturé dans MRC): ${staleAlerts.map((b) => b.id).join(', ')}`
              );
              staleAlerts.forEach((b) => {
                fetch(`${BACKEND}/api/mrc-alerts/${encodeURIComponent(b.id)}`, {
                  method: 'DELETE',
                  headers: { 'X-MRC-Auth': auth },
                }).catch((e) => console.warn('[PendingMRC] DELETE stale:', e.message));
              });
            }
          }

          set((state) => {
            // Garder les alertes locales pending_upload :
            // - non encore confirmées sur le backend
            // - ET dont le contrat est encore actif (si activeContractIds fourni)
            const localPending = state.pendingReturns.filter(
              (r) =>
                r.status === 'pending_upload' &&
                !activeBackendAlerts.some((b) => b.id === r.id) &&
                (!activeContractIds || activeContractIds.includes(String(r.id)))
            );

            // Mapper les alertes backend actives
            const confirmed: PendingReturn[] = activeBackendAlerts.map((b) => ({
              id: b.id,
              plate: b.plate,
              vehicleName: b.vehicle_name,
              clientName: b.client_name,
              agencyCode: b.agency_code,
              completedAt: b.completed_at,
              agentName: b.agent_name,
              kmDepart: b.km_depart,
              kmRetour: b.km_retour,
              fuelDepart: b.fuel_depart,
              fuelRetour: b.fuel_retour,
              status: 'confirmed' as const,
            }));

            return { pendingReturns: [...localPending, ...confirmed] };
          });
        } catch (e) {
          console.warn('[PendingMRC] fetchGlobalAlerts:', (e as Error).message);
        }
      },

      // Purge ciblée après sync : uniquement pour les agencyCodes synchronisés
      cleanupAfterSync: async (agencyCodes, activeContractIds) => {
        const auth = getAuthHeader();
        if (!auth || !navigator.onLine) return;
        try {
          await fetch(`${BACKEND}/api/mrc-alerts/cleanup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-MRC-Auth': auth,
            },
            body: JSON.stringify({ agencyCodes, activeContractIds }),
          });

          // ✅ FIX — Purge locale : supprimer les alertes dont le contrat n'est plus actif
          // String() obligatoire pour éviter les mismatch numérique/string
          set((state) => ({
            pendingReturns: state.pendingReturns.filter((r) => {
              // Agence non synchronisée → on ne touche pas
              if (!agencyCodes.includes(r.agencyCode)) return true;
              // Agence synchronisée → garder seulement si contrat encore dans MRC
              return activeContractIds.includes(String(r.id));
            }),
          }));

          // ✅ FIX — fetchGlobalAlerts reçoit activeContractIds pour supprimer
          // les alertes backend périmées ET éviter qu'elles réapparaissent
          await get().fetchGlobalAlerts(agencyCodes, activeContractIds);
        } catch (e) {
          console.warn('[PendingMRC] cleanupAfterSync:', (e as Error).message);
        }
      },
    }),
    {
      name: 'rentalcheck-pending-mrc-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
